import { z } from 'zod';

import { setItemInLocalStorage } from '@/shared-utilities';

export const TELEMETRY_CONSENT_COOKIE_NAME = 'cc_cookie';
export const TELEMETRY_CONSENT_CHANGED_EVENT = 'telemetry-consent-changed';
export const TELEMETRY_CONSENT_STORAGE_KEY =
  'theysaid-telemetry-consent-changed-at';

export interface TelemetryConsentState {
  analytics: boolean;
  recordings: boolean;
  hasChoice: boolean;
}

export const DEFAULT_TELEMETRY_CONSENT_STATE: TelemetryConsentState = {
  analytics: false,
  recordings: false,
  hasChoice: false,
};

const consentCookieValueSchema = z.looseObject({
  categories: z.array(z.string()).optional(),
});

type ConsentCookieValue = z.infer<typeof consentCookieValueSchema>;

function parseCookieValue(cookieValue: string): ConsentCookieValue | null {
  try {
    const parsedCookie: unknown = JSON.parse(decodeURIComponent(cookieValue));
    const result = consentCookieValueSchema.safeParse(parsedCookie);

    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function getConsentCookieValue(cookieString: string): string | null {
  const cookies = cookieString.split(';');
  const consentCookie = cookies.find((cookie) =>
    cookie.trim().startsWith(`${TELEMETRY_CONSENT_COOKIE_NAME}=`)
  );

  if (!consentCookie) return null;

  return consentCookie.trim().slice(TELEMETRY_CONSENT_COOKIE_NAME.length + 1);
}

export function getTelemetryConsentStateFromCookieString(
  cookieString: string
): TelemetryConsentState {
  const cookieValue = getConsentCookieValue(cookieString);

  if (!cookieValue) {
    return DEFAULT_TELEMETRY_CONSENT_STATE;
  }

  const parsedCookie = parseCookieValue(cookieValue);
  const categories = parsedCookie?.categories;

  if (!Array.isArray(categories)) {
    return DEFAULT_TELEMETRY_CONSENT_STATE;
  }

  return {
    analytics: categories.includes('analytics'),
    recordings: categories.includes('recordings'),
    hasChoice: true,
  };
}

export function getTelemetryConsentState(): TelemetryConsentState {
  if (typeof document === 'undefined') {
    return DEFAULT_TELEMETRY_CONSENT_STATE;
  }

  let cookieString: string;

  try {
    cookieString = document.cookie;
  } catch {
    // Sandboxed iframe without allow-same-origin throws SecurityError on cookie access.
    return DEFAULT_TELEMETRY_CONSENT_STATE;
  }

  return getTelemetryConsentStateFromCookieString(cookieString);
}

export function notifyTelemetryConsentChanged(state: TelemetryConsentState) {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent<TelemetryConsentState>(TELEMETRY_CONSENT_CHANGED_EVENT, {
      detail: state,
    })
  );

  try {
    setItemInLocalStorage(TELEMETRY_CONSENT_STORAGE_KEY, Date.now());
  } catch {
    // Storage can be unavailable in private or embedded contexts.
  }

  try {
    const broadcastChannel = new BroadcastChannel(
      TELEMETRY_CONSENT_CHANGED_EVENT
    );
    // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel does not take targetOrigin
    broadcastChannel.postMessage(state);
    broadcastChannel.close();
  } catch {
    // BroadcastChannel is unavailable in some browsers and iframe contexts.
  }
}

export function isEmbeddedFrame(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}
